import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";

import Sidebar from "../../components/subscription/sidebar/Sidebar";
import CompanyStep from "./steps/CompanyStep";
import AccountStep from "./steps/AccountStep";
import PaymentStep from "./steps/PaymentStep";

import PaymentProgressModal from "../../components/subscription/PaymentProgressModal";
import SuccessModal from "../../components/subscription/SuccessModal";
import ErrorModal from "../../components/subscription/ErrorModal";

import { useGuestToken } from "../../hook/Subscription/useGuestToken";
import { useProject } from "../../hook/Subscription/useProject";
import { useProjectPlans } from "../../hook/Subscription/useProjectPlans";
import { useSubscriptionFlow } from "../../hook/Subscription/useSubscriptionFlow";
import { useSubmitSubscription } from "../../hook/Subscription/useSubmitSubscription";
import { usePaymentConfig } from "../../hook/Subscription/usePaymentConfig";
import { useTermsPrivacyRefund } from "../../hook/Subscription/useTermsPrivacyRefund";

import { STEP, getExpiredRedirectUrl } from "./config/projectConfig";

/**
 * SubscriptionFlow — orchestrator.
 *
 * Layout:
 *  - Desktop (>768px): 100vh full screen, sidebar left + step area right.
 *    Internal scroll inside StepShell so the page chrome stays fixed.
 *  - Mobile (≤768px):  Normal page flow — sidebar stacked above step,
 *    page-level scroll (no internal scroll containers). This is what
 *    users expect on touch devices.
 */
function SubscriptionFlow({
  id_project,
  initialPlanId,
  initialPromo = "",
  initialRef = "",
  lang = "th",
}) {
  const guestToken = useGuestToken({ id_project });
  const { data: project } = useProject({ id_project });
  const { data: plans = [] } = useProjectPlans({ id_project });

  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (selectedPlan || plans.length === 0) return;
    const fromUrl = plans.find(
      (p) => Number(p.id_subscription_plan_table) === Number(initialPlanId)
    );
    setSelectedPlan(fromUrl ?? plans[0]);
  }, [plans, initialPlanId, selectedPlan]);

  const [promo, setPromo] = useState(null);
  const [referral, setReferral] = useState(null);

  const handlePromoChange = useCallback((data) => setPromo(data), []);
  const handleReferralChange = useCallback((data) => setReferral(data), []);
  const handlePlanChange = useCallback((plan) => setSelectedPlan(plan), []);

  const {
    step,
    steps,
    formData,
    submitStep,
    goBack,
  } = useSubscriptionFlow({ id_project });

  const submitSubscription = useSubmitSubscription();
  const paymentConfig = usePaymentConfig(id_project);
  const policies = useTermsPrivacyRefund({ id_project });

  const handleFinalSubmit = useCallback(
    async (paymentData) => {
      submitStep(paymentData);
      try {
        // Pull existingAuth out of the account payload — it was added
        // by AccountStep when the user signed in via the modal. The
        // token rides as a separate `bearerToken` arg to the submit
        // hook (which forwards it to the API as the
        // x-has-accesstoken header). We strip existingAuth from the
        // account object so it doesn't end up in the request body
        // and accidentally get logged.
        const { existingAuth, ...accountPayload } = formData.account || {};

        await submitSubscription.submit({
          id_project,
          id_subscription_plan_table: selectedPlan?.id_subscription_plan_table,
          omise_public_key: paymentConfig.data?.omise?.public_key,
          company: formData.company,
          account: accountPayload,
          payment: paymentData,
          promo,
          referral,
          bearerToken: existingAuth?.token || null,
        });
      } catch {
        // Error already in submitSubscription.error
      }
    },
    [
      submitStep,
      submitSubscription,
      id_project,
      selectedPlan,
      paymentConfig.data,
      formData.company,
      formData.account,
      promo,
      referral,
    ]
  );

  const handleErrorRetry = useCallback(() => {
    submitSubscription.reset();
  }, [submitSubscription]);

  // ─── Loading / fatal guards ──────────────────────────
  if (!guestToken.isReady && !guestToken.error) {
    return (
      <Center>
        <Spinner animation="border" variant="primary" />
      </Center>
    );
  }

  if (paymentConfig.error) {
    return (
      <Center>
        <FatalTitle>
          {lang === "en" ? "Payment unavailable" : "ระบบชำระเงินไม่พร้อมใช้งาน"}
        </FatalTitle>
        <FatalSub>
          {lang === "en"
            ? "Please try again later or contact support."
            : "กรุณาลองใหม่ภายหลัง หรือติดต่อทีมสนับสนุน"}
        </FatalSub>
      </Center>
    );
  }

  if (guestToken.isExpired) {
    return (
      <Center>
        <FatalTitle>
          {lang === "en" ? "Session expired" : "เซสชันหมดอายุ"}
        </FatalTitle>
        <FatalSub>
          {lang === "en"
            ? "Please return to the previous page to try again."
            : "กรุณากลับไปหน้าก่อนหน้าเพื่อลองใหม่"}
        </FatalSub>
        <FatalLink href={getExpiredRedirectUrl(id_project)}>
          {lang === "en" ? "Go back" : "กลับ"}
        </FatalLink>
      </Center>
    );
  }

  // ─── Step renderer ──────────────────────────────
  const renderStep = () => {
    switch (step) {
      case STEP.COMPANY:
        return (
          <CompanyStep
            id_project={id_project}
            defaultValues={formData.company}
            onSubmit={submitStep}
            lang={lang}
          />
        );
      case STEP.ACCOUNT:
        return (
          <AccountStep
            id_project={id_project}
            projectName={project?.name_project}
            defaultValues={{
              ...formData.account,
              id_subscription_plan_table:
                formData.account.id_subscription_plan_table ??
                selectedPlan?.id_subscription_plan_table,
            }}
            onSubmit={submitStep}
            onBack={goBack}
            onPlanChange={handlePlanChange}
            lang={lang}
          />
        );
      case STEP.PAYMENT:
        return (
          <PaymentStep
            plan={selectedPlan}
            promo={promo}
            onSubmit={handleFinalSubmit}
            onBack={goBack}
            terms={policies.terms}
            privacy={policies.privacy}
            refund={policies.refund}
            lang={lang}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Page>
      <Layout>
        <SidebarSlot>
          <Sidebar
            id_project={id_project}
            selectedPlan={selectedPlan}
            steps={steps}
            currentStep={step}
            initialPromo={initialPromo}
            initialRef={initialRef}
            onPromoChange={handlePromoChange}
            onReferralChange={handleReferralChange}
            lang={lang}
          />
        </SidebarSlot>

        <StepSlot>
          <StepShell>{renderStep()}</StepShell>
        </StepSlot>
      </Layout>

      <PaymentProgressModal
        show={submitSubscription.isPending}
        phase={submitSubscription.phase}
        lang={lang}
      />

      <SuccessModal
        show={submitSubscription.isSuccess}
        amount={selectedPlan?.price}
        projectEmail={formData.account?.project_email}
        billingEmail={formData.account?.email}
        redirectUrl={getExpiredRedirectUrl(id_project)}
        lang={lang}
      />

      <ErrorModal
        show={!!submitSubscription.error}
        error={submitSubscription.error}
        onClose={() => submitSubscription.reset()}
        onRetry={handleErrorRetry}
        lang={lang}
      />
    </Page>
  );
}

export default SubscriptionFlow;

// ─── Styled ──────────────────────────────────

/**
 * Page — outermost wrapper.
 *
 * Desktop: locked to viewport height + overflow:hidden so the app feels
 * like a fixed-chrome dashboard (sidebar + step area never scroll the
 * page itself).
 *
 * Mobile: released to auto-height + min-height:100vh so the page scrolls
 * normally like any web page. min-height keeps the bg color filling the
 * viewport when content is short.
 */
const Page = styled.div`
  height: 100vh;
  background: #f8f9fb;
  padding: 16px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    padding: 12px;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  height: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto; /* both auto — let content size them */
    height: auto;
    gap: 12px;
  }
`;

const SidebarSlot = styled.div`
  height: 100%;
  min-height: 0;

  @media (max-width: 768px) {
    height: auto;
  }
`;

const StepSlot = styled.div`
  height: 100%;
  min-height: 0;

  @media (max-width: 768px) {
    height: auto;
  }
`;

/**
 * StepShell — the white card holding the active step.
 *
 * Desktop: fixed height with overflow:hidden so the step's own internal
 * scroll (Body) handles overflow.
 *
 * Mobile: auto-height so the card grows to fit content and the page
 * scrolls naturally. The step's internal Body should also drop its
 * overflow:auto on mobile (see AccountStep's Body media query).
 */
const StepShell = styled.div`
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    overflow: visible;
  }
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100vh;
  padding: 16px;
  gap: 12px;
  background: #f8f9fb;
`;

const FatalTitle = styled.h5`
  && {
    font-weight: 700;
    font-size: 18px;
    color: #111827;
    margin: 0;
  }
`;

const FatalSub = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const FatalLink = styled.a`
  && {
    margin-top: 4px;
    background: #6366f1;
    color: #ffffff;
    padding: 10px 24px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      background: #4f46e5;
      color: #ffffff;
    }
  }
`;